import { useState, useMemo } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Search,
  MoreVertical,
  Edit,
  Star,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Settings,
} from "lucide-react"
import { toast } from "sonner"
import { formatDate } from "@/lib/utils"
import { useCustomerAddressesList, useCustomerAddresses } from "@/hooks/useCustomerAddresses"
import { useCustomerContactsList } from "@/hooks/useCustomerContacts"
import { ColumnConfigDialog, type ColumnDefinition } from "@/components/shared/column-config-dialog"
import { useScreenColumnPreferences } from "@/hooks/useScreenColumnPreferences"
import { UniqueTableNamesForColumnConfiguration } from "@/config/unique-table-names-for-column-configuration"
import type { CustomerAddressUI } from "@/types/ui/customer-detail"

const DEFAULT_ADDRESS_COLUMNS: ColumnDefinition[] = [
  { id: 'primary', label: 'Primary', visible: true, order: 0 },
  { id: 'statement', label: 'Statement', visible: true, order: 1 },
  { id: 'invoice', label: 'Invoice', visible: true, order: 2 },
  { id: 'delivery', label: 'Delivery', visible: true, order: 3 },
  { id: 'deliveryDefault', label: 'Delivery Default', visible: true, order: 4 },
  { id: 'contact', label: 'Contact', visible: true, order: 5 },
  { id: 'address', label: 'Address', visible: true, order: 6 },
  { id: 'town', label: 'Town', visible: true, order: 7 },
  { id: 'county', label: 'County', visible: true, order: 8 },
  { id: 'postcode', label: 'Postcode', visible: true, order: 9 },
  { id: 'country', label: 'Country', visible: true, order: 10 },
  { id: 'created', label: 'Created', visible: true, order: 11 },
]

interface AddressesTabProps {
  readonly customerId: string
  readonly customerName: string
  readonly primaryAddress?: string
}

type SortField = "city" | "state" | "country" | "createdAt"
type SortDirection = "asc" | "desc"

interface AddressFormData {
  address: string
  city: string
  state: string
  postalCode: string
  country: string
  countryCode: string
  notes: string
  isInvoice: boolean
  isStatement: boolean
  isDelivery: boolean
  isPrimary: boolean
  contactId: string
}

const INITIAL_FORM_DATA: AddressFormData = {
  address: "",
  city: "",
  state: "",
  postalCode: "",
  country: "",
  countryCode: "",
  notes: "",
  isInvoice: false,
  isStatement: false,
  isDelivery: false,
  isPrimary: false,
  contactId: "",
}

export function AddressesTab({ customerId }: AddressesTabProps) {
  // API hooks
  const { data: apiAddresses = [], isLoading } = useCustomerAddressesList(customerId)
  const { updateAddress, setAddressAsPrimary } = useCustomerAddresses(customerId)
  const { data: contacts = [] } = useCustomerContactsList(customerId)

  const [searchQuery, setSearchQuery] = useState("")
  const [sortField, setSortField] = useState<SortField>("city")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)

  // Column preferences
  const {
    columns: addrColumns,
    visibleColumns: addrVisibleColumns,
    savePreferences: saveAddrColumnPreferences,
    resetToDefaults: resetAddrColumnPreferences,
  } = useScreenColumnPreferences(UniqueTableNamesForColumnConfiguration.CustomerDetailAddresses, DEFAULT_ADDRESS_COLUMNS)

  // Dialog states
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<CustomerAddressUI | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  // Form states
  const [formData, setFormData] = useState<AddressFormData>(INITIAL_FORM_DATA)

  // Filter and sort addresses
  const filteredAddresses = useMemo(() => {
    let filtered = [...apiAddresses]

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (x) =>
          (x.addressLine || "").toLowerCase().includes(query) ||
          (x.town || "").toLowerCase().includes(query) ||
          (x.county || "").toLowerCase().includes(query) ||
          (x.postCode || "").includes(query) ||
          (x.country || "").toLowerCase().includes(query)
      )
    }

    filtered.sort((a, b) => {
      const fieldMap: Record<SortField, string> = {
        city: "town",
        state: "county",
        country: "country",
        createdAt: "createdAt",
      }
      const key = fieldMap[sortField] as keyof CustomerAddressUI
      let aValue = (a[key] as string) || ""
      let bValue = (b[key] as string) || ""

      aValue = aValue.toLowerCase()
      bValue = bValue.toLowerCase()

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
      return 0
    })

    return filtered
  }, [apiAddresses, searchQuery, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    )
  }

  const handleEditAddress = async () => {
    if (!selectedAddress) return

    setIsSaving(true)
    try {
      await updateAddress(selectedAddress.id, {
        isPrimary: formData.isPrimary,
        isStatement: formData.isStatement,
        contactId: formData.contactId ? Number(formData.contactId) : undefined,
      })
      setIsEditDialogOpen(false)
      setSelectedAddress(null)
      resetForm()
      toast.success("Address updated successfully")
    } catch {
      toast.error("Failed to update address")
    } finally {
      setIsSaving(false)
    }
  }

  const handleSetPrimary = async (address: CustomerAddressUI) => {
    try {
      await setAddressAsPrimary(address.id)
      toast.success("Primary address updated")
    } catch {
      toast.error("Failed to update primary address")
    }
  }

  const openEditDialog = (address: CustomerAddressUI) => {
    setSelectedAddress(address)
    setFormData({
      address: address.addressLine || "",
      city: address.town || "",
      state: address.county || "",
      postalCode: address.postCode || "",
      country: address.country || "",
      countryCode: address.countryCode || "",
      notes: address.notes || "",
      isInvoice: address.isInvoice,
      isStatement: address.isStatement,
      isDelivery: address.isDelivery,
      isPrimary: address.isPrimary,
      contactId: address.contactId ? String(address.contactId) : "",
    })
    setIsEditDialogOpen(true)
  }

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA)
  }

  // Render table header for a column (with sort support where applicable)
  const renderColumnHeader = (columnId: string, label: string) => {
    const sortableMap: Record<string, SortField> = {
      town: 'city',
      county: 'state',
      country: 'country',
      created: 'createdAt',
    }
    const sortKey = sortableMap[columnId]
    if (sortKey) {
      return (
        <Button
          variant="ghost"
          onClick={() => handleSort(sortKey)}
          className="h-8 px-2 lg:px-3"
        >
          {label}
          {getSortIcon(sortKey)}
        </Button>
      )
    }
    return label
  }

  // Render cell content based on column ID
  const renderCellContent = (columnId: string, address: CustomerAddressUI) => {
    switch (columnId) {
      case 'primary':
        return address.isPrimary ? (
          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
        ) : null
      case 'statement':
        return <Checkbox checked={address.isStatement} disabled className="cursor-default" />
      case 'invoice':
        return <Checkbox checked={address.isInvoice} disabled className="cursor-default" />
      case 'delivery':
        return <Checkbox checked={address.isDelivery} disabled className="cursor-default" />
      case 'deliveryDefault':
        return <Checkbox checked={address.isDeliveryDefault} disabled className="cursor-default" />
      case 'contact': {
        const contact = contacts.find((x) => String(x.id) === String(address.contactId))
        return contact ? `${contact.firstName} ${contact.surname}` : ""
      }
      case 'address':
        return (
          <div className="space-y-0.5">
            <div className="font-medium truncate">{address.addressLine}</div>
          </div>
        )
      case 'town':
        return address.town
      case 'county':
        return address.county
      case 'postcode':
        return address.postCode
      case 'country':
        return address.country
      case 'created':
        return formatDate(address.createdAt)
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-muted-foreground">
        Loading addresses...
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header with Search and Add Button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search addresses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setColumnConfigOpen(true)}
                  aria-label="Configure Columns"
                >
                  <Settings className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Configure Columns</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Addresses Table */}
      <div className="rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              {addrVisibleColumns.map((column) => (
                <TableHead key={column.id} className={column.id === 'primary' ? 'w-[50px]' : undefined}>
                  {renderColumnHeader(column.id, column.label)}
                </TableHead>
              ))}
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAddresses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={addrVisibleColumns.length + 1} className="text-center text-muted-foreground">
                  No addresses found
                </TableCell>
              </TableRow>
            ) : (
              filteredAddresses.map((address) => (
                <TableRow key={address.id}>
                  {addrVisibleColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      className={column.id === 'address' ? 'max-w-[250px] overflow-hidden text-ellipsis' : undefined}
                      title={column.id === 'address' ? address.addressLine : undefined}
                    >
                      {renderCellContent(column.id, address)}
                    </TableCell>
                  ))}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!address.isPrimary && (
                          <DropdownMenuItem onClick={() => handleSetPrimary(address)}>
                            <Star className="mr-2 h-4 w-4" />
                            Set as Primary
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => openEditDialog(address)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Column Config Dialog */}
      <ColumnConfigDialog
        columns={addrColumns}
        onApply={saveAddrColumnPreferences}
        onReset={resetAddrColumnPreferences}
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
      />

      {/* Edit Address Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Address</DialogTitle>
            <DialogDescription>
              Update address flags and contact. Primary, Statement, and Contact can be modified.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Address Type Flags</Label>
              <div className="flex items-center gap-6">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-isStatement"
                    checked={formData.isStatement}
                    onChange={(e) => setFormData({ ...formData, isStatement: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <Label htmlFor="edit-isStatement" className="cursor-pointer">
                    Statement
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-isInvoice"
                    checked={formData.isInvoice}
                    disabled
                    className="h-4 w-4 rounded border-gray-300 opacity-50"
                  />
                  <Label htmlFor="edit-isInvoice" className="cursor-pointer opacity-50">
                    Invoice
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-isDelivery"
                    checked={formData.isDelivery}
                    disabled
                    className="h-4 w-4 rounded border-gray-300 opacity-50"
                  />
                  <Label htmlFor="edit-isDelivery" className="cursor-pointer opacity-50">
                    Delivery
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-isDeliveryDefault"
                    checked={selectedAddress?.isDeliveryDefault ?? false}
                    disabled
                    className="h-4 w-4 rounded border-gray-300 opacity-50"
                  />
                  <Label htmlFor="edit-isDeliveryDefault" className="cursor-pointer opacity-50">
                    Default Delivery
                  </Label>
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-contact">Contact</Label>
              <Select
                value={formData.contactId}
                onValueChange={(value) => setFormData({ ...formData, contactId: value === "none" ? "" : value })}
              >
                <SelectTrigger id="edit-contact">
                  <SelectValue placeholder="Select a contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {contacts.map((x) => (
                    <SelectItem key={x.id} value={String(x.id)}>
                      {`${x.firstName} ${x.surname}`}{x.isPrimary ? " (Primary)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                value={formData.address}
                readOnly
                className="opacity-50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-city">Town</Label>
                <Input
                  id="edit-city"
                  value={formData.city}
                  readOnly
                  className="opacity-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-state">County</Label>
                <Input
                  id="edit-state"
                  value={formData.state}
                  readOnly
                  className="opacity-50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-postalCode">Postcode</Label>
              <Input
                id="edit-postalCode"
                value={formData.postalCode}
                readOnly
                className="opacity-50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-countryCode">Country Code</Label>
                <Input
                  id="edit-countryCode"
                  value={formData.countryCode}
                  readOnly
                  className="opacity-50"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-country">Country</Label>
                <Input
                  id="edit-country"
                  value={formData.country}
                  readOnly
                  className="opacity-50"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                readOnly
                className="opacity-50"
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-isPrimary"
                checked={formData.isPrimary}
                onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="edit-isPrimary" className="cursor-pointer">
                Set As Primary
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditAddress} disabled={isSaving}>
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
